import React, { useState, useEffect, useRef, KeyboardEvent, ChangeEvent } from 'react';
import { Minus, Plus } from 'lucide-react';

export type StepperSize = 'sm' | 'md' | 'lg';

export interface ExactDurationStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  size?: StepperSize;
  disabled?: boolean;
  className?: string;
  label?: string;
  backStep?:number | null;
}

interface SizeStyles {
  button: string;
  display: string;
  icon: string;
  numText: string;
  unitText: string;
}

export const ExactDurationStepper: React.FC<ExactDurationStepperProps> = ({ 
  value, 
  onChange, 
  min = 5, 
  max = 180, 
  step = 5,
  size = 'md',
  disabled = false,
  className = "",
  label ="Mins",
  backStep =null
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<number | string>(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDecrement = (): void => {
    if (disabled) return;
    const newValue = Math.max(min, value - (backStep || step));
    onChange(newValue);
  };

  const handleIncrement = (): void => {
    if (disabled) return;
    const newValue = Math.min(max, value + step);
    onChange(newValue);
  };

  const handleInputSubmit = (): void => {
    setIsEditing(false);
    let num = parseInt(String(inputValue), 10);
    if (isNaN(num)) num = value;
    num = Math.max(min, Math.min(max, num));
    onChange(num);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      handleInputSubmit();
    } else if (e.key === 'Escape') {
      setInputValue(value);
      setIsEditing(false);
    }
  };

  const sizeClasses: Record<StepperSize, SizeStyles> = {
    sm: {
      button: 'w-8 h-8 rounded-lg text-xs',
      display: 'h-8 px-3 rounded-lg text-sm',
      icon: 'w-3.5 h-3.5',
      numText: 'text-base font-bold',
      unitText: 'text-xs font-normal'
    },
    md: {
      button: 'w-11 h-11 rounded-xl text-base',
      display: 'h-11 px-6 rounded-xl text-base',
      icon: 'w-4 h-4',
      numText: 'text-xl font-bold',
      unitText: 'text-sm font-normal'
    },
    lg: {
      button: 'w-14 h-[50px] rounded-2xl text-lg',
      display: 'h-[50px] px-8 rounded-2xl text-lg',
      icon: 'w-8 h-8',
      numText: 'text-2xl font-extrabold',
      unitText: 'text-base font-normal'
    }
  };

  const currentSize = sizeClasses[size] || sizeClasses.md;

  return (
    <div className={`inline-flex w-full items-center gap-2 select-none ${className}`}>
      {/* Minus Button */}
      <button
        type="button"
        onClick={handleDecrement}
        disabled={disabled || value <= min}
        aria-label="Decrease time"
        className={`
          ${currentSize.button}
          flex items-center justify-center
          bg-white dark:bg-slate-900 
          border border-gray-200 dark:border-slate-700 
          text-slate-700 dark:text-slate-300
          hover:bg-gray-50 dark:hover:bg-slate-800 
          active:bg-gray-100 dark:active:bg-slate-700
          disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white
          transition-all duration-150 shadow-sm
          focus:outline-none focus:ring-2 focus:ring-indigo-500/20
        `}
      >
        <Minus className={`${currentSize.icon} stroke-[2.2]`} size={30}/>
      </button>

      {/* Center Value Display */}
      <div 
        //onClick={() => !disabled && setIsEditing(true)}
        title="Click to manually edit duration"
        className={`
          ${currentSize.display}
          grow min-w-[140px]
          flex items-center justify-center
          bg-white dark:bg-slate-900 
          border border-gray-200 dark:border-slate-700
          shadow-sm transition-all duration-150 cursor-pointer
          hover:border-indigo-300 dark:hover:border-indigo-600
        `}
      >
        {isEditing ? (
          <div className="flex items-center justify-center gap-1">
            <input
              ref={inputRef}
              type="number"
              value={inputValue}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
              onBlur={handleInputSubmit}
              onKeyDown={handleKeyDown}
              className="w-16 text-center font-bold text-[#1e1b4b] dark:text-indigo-200 bg-transparent focus:outline-none text-xl"
              min={min}
              max={max}
            />
            <span className="text-slate-500 text-sm font-medium">{label}</span>
          </div>
        ) : (
          <div className="flex items-baseline gap-0.5 text-[#1e1b4b] dark:text-slate-100">
            <span className={`${currentSize.numText} tracking-tight`}>
              {value}
            </span>
            <span className={`${currentSize.unitText} text-[#333763] dark:text-slate-400 font-medium ml-0.5`}>
              {label}
            </span>
          </div>
        )}
      </div>

      {/* Plus Button */}
      <button
        type="button"
        onClick={handleIncrement}
        disabled={disabled || value >= max}
        aria-label="Increase time"
        className={`
          ${currentSize.button}
          flex items-center justify-center
          bg-white dark:bg-slate-900 
          border border-gray-200 dark:border-slate-700 
          text-slate-700 dark:text-slate-300
          hover:bg-gray-50 dark:hover:bg-slate-800 
          active:bg-gray-100 dark:active:bg-slate-700
          disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white
          transition-all duration-150 shadow-sm
          focus:outline-none focus:ring-2 focus:ring-indigo-500/20
        `}
      >
        <Plus className={`${currentSize.icon} stroke-[2.2]`} />
      </button>
    </div>
  );
};
